import { Icons, Stack, Grid } from '@/theme/index';
import {
  GridItem,
  TileItem,
  TallStack,
  ThreatItem,
  VerticalDivider,
  SectionTitle,
} from '../components';
import {
  getFraudTileText,
  getThreatLevel,
  getThreatLevelTitle,
} from '../utils';

export interface FraudPhoneOverviewProps {
  carrier: string;
  userActivity: string;
  lineType: string;
  activeStatus: string;
  isLeaked?: boolean;
  isVOIP?: boolean;
  isPrepaid?: boolean;
  isValid?: boolean;
  isActive?: boolean;
  isDoNotCall?: boolean;
}

export function FraudPhoneOverview({
  carrier,
  userActivity,
  lineType,
  activeStatus,
  isVOIP,
  isPrepaid,
  isLeaked,
  isValid,
  isActive,
  isDoNotCall,
}: FraudPhoneOverviewProps) {
  return (
    <Stack>
      <SectionTitle>Overview</SectionTitle>
      <TallStack
        mb={1}
        width="100%"
        alignItems="center"
        height={{ xs: '95px', md: '118px' }}
        direction="row"
      >
        <ThreatItem
          text={getThreatLevelTitle(
            isValid,
            'Valid',
            'Invalid',
            'Valid Status Unknown'
          )}
          tooltipText="Indicates whether the phone number is properly formatted and considered valid based on assigned phone numbers available to carriers in the country associated with its country code."
          threatLevel={getThreatLevel(
            isValid === undefined ? undefined : !isValid
          )}
          width={isDoNotCall ? '33%' : '50%'}
        />
        <VerticalDivider />
        <ThreatItem
          text={getThreatLevelTitle(
            isActive,
            'Active',
            'Inactive',
            'Active Status Unknown'
          )}
          tooltipText="Indicates whether the number is likely to be a live, usable phone number that is currently active."
          threatLevel={getThreatLevel(
            isValid === undefined ? undefined : !isActive
          )}
          width={isDoNotCall ? '33%' : '50%'}
        />
        {isDoNotCall && (
          <>
            <VerticalDivider />
            <ThreatItem
              text="Do Not Call"
              tooltipText="Indicates if the phone number is listed on national Do Not Call (DNC) lists within the US or Canada. This data may not be 100% up to date with the latest DNC lists."
              threatLevel={getThreatLevel(isDoNotCall)}
              width="33%"
            />
          </>
        )}
      </TallStack>
      <Grid container spacing={2}>
        <GridItem>
          <TileItem
            title="Carrier:"
            text={carrier}
            IconComponent={Icons.Wifi}
            testId="fraud-phone-overview-carrier"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Line Type:"
            text={lineType}
            IconComponent={Icons.Smartphone}
            testId="fraud-phone-overview-line-type"
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="VOIP:"
            text={getFraudTileText(isVOIP)}
            IconComponent={Icons.Waves}
            testId="fraud-phone-overview-voip"
            tooltipText="VOIP (Voice Over Internet Protocol) numbers allow callers to make and receive calls over the internet rather than traditional phone lines. They are commonly used by spammers and scammers. However, they can also be used by legitimate callers or businesses."
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Prepaid:"
            text={getFraudTileText(isPrepaid)}
            IconComponent={Icons.Smartphone}
            testId="fraud-phone-overview-prepaid"
            tooltipText="Indicates that this number is likely to be associated with a prepaid service plan."
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="User Activity:"
            text={userActivity}
            IconComponent={Icons.Summarize}
            testId="fraud-phone-overview-user-activity"
            tooltipText="Reflects the frequency at which this phone number makes legitimate purchases, registrations, and engages in legitimate user behavior online. Higher usage suggests a higher likelihood that a number is legitimate and not associated with abuse or fraud. New phone numbers that lack usage history will likely report 'None' for user activity."
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Active Status:"
            text={activeStatus}
            IconComponent={Icons.Smartphone}
            testId="fraud-phone-overview-active-status"
            tooltipText="Reports whether the phone number is likely to be active, disconnected, turned off, or if the status is unknown."
          />
        </GridItem>

        <GridItem>
          <TileItem
            title="Leaked in Data Breach:"
            text={getFraudTileText(isLeaked)}
            IconComponent={Icons.VerifiedUser}
            testId="fraud-phone-overview-leaked"
            tooltipText="Indicates if the phone number has likely been exposed in a known online data breach."
          />
        </GridItem>
      </Grid>
    </Stack>
  );
}
